import React, { useCallback, useMemo } from 'react';
import { NumericInput } from '../../../../../components/shared/ui/NumericInput';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import {
  setNetsuiteItemEnabled,
  updateNetsuiteItemQuantity,
} from '../../../../../store/slices/createCRSlice';
import { selectAutoAddedNetsuiteItems } from '../../../../../store/selectors/createCRSelectors';
import { findMissingEnphaseAccessories } from '../../../../../utils/enphase-netsuite-items.utils';
import { NetsuiteItemResponse } from '../../../../../types/netsuite-items.types';

interface EnphaseItemsSectionProps {
  /** Client NetSuite catalog — used to warn about accessories missing from the database */
  catalogItems: NetsuiteItemResponse[];
  isEnabled: boolean;
}

/**
 * Auto-added Enphase accessory items (Q Cable parts) for projects with Enphase
 * microinverters. Items are real client NetSuite database entries selected
 * automatically; quantities follow the LGCY BOM calculation unless manually edited.
 * Items (individually or the whole set) can be disabled to exclude them from exports.
 */
export const EnphaseItemsSection: React.FC<EnphaseItemsSectionProps> = ({
  catalogItems,
  isEnabled,
}) => {
  const dispatch = useAppDispatch();
  const items = useAppSelector(selectAutoAddedNetsuiteItems);

  const missingExternalIds = useMemo(
    () => (catalogItems.length > 0 ? findMissingEnphaseAccessories(catalogItems) : []),
    [catalogItems],
  );

  const allItemsEnabled = useMemo(() => items.every(item => item.enabled), [items]);

  const handleQuantityChange = useCallback((id: string, quantity: number) => {
    dispatch(updateNetsuiteItemQuantity({ id, quantity, markEdited: true }));
  }, [dispatch]);

  const handleToggleItem = useCallback((id: string, enabled: boolean) => {
    dispatch(setNetsuiteItemEnabled({ id, enabled }));
  }, [dispatch]);

  const handleToggleAll = useCallback(() => {
    const nextEnabled = !allItemsEnabled;
    items.forEach(item => dispatch(setNetsuiteItemEnabled({ id: item.id, enabled: nextEnabled })));
  }, [items, allItemsEnabled, dispatch]);

  if (items.length === 0 && missingExternalIds.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 3xl:px-5 bg-orange-50 border-b border-orange-100">
        <svg className="w-3.5 h-3.5 text-orange-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <span className="text-[10px] 3xl:text-[11px] uppercase tracking-widest font-bold text-slate-700 shrink-0">Enphase Items</span>
        <span className="rounded-full border border-orange-200 bg-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-orange-600 shrink-0">
          Auto
        </span>
        <span className="ml-auto text-[10px] text-slate-400 hidden sm:block truncate">
          Enphase project — predefined items added automatically, double-check quantities
        </span>
        {items.length > 0 && (
          <button
            type="button"
            onClick={handleToggleAll}
            disabled={!isEnabled}
            className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              allItemsEnabled ? 'bg-orange-500' : 'bg-slate-300'
            }`}
            title={allItemsEnabled ? 'Disable all Enphase items' : 'Enable all Enphase items'}
          >
            <span className={`inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${
              allItemsEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
            }`} />
          </button>
        )}
      </div>

      <div className="p-4 space-y-1.5">
        {items.map(item => (
          <div
            key={item.id}
            className={`flex items-center gap-2 rounded-lg border border-orange-100 px-2.5 py-1.5 transition-colors ${
              item.enabled ? 'bg-orange-50/40 hover:bg-orange-50/70' : 'bg-slate-50/60 opacity-60'
            }`}
          >
            <input
              type="checkbox"
              checked={item.enabled}
              onChange={(e) => handleToggleItem(item.id, e.target.checked)}
              disabled={!isEnabled}
              className="shrink-0 w-3.5 h-3.5 rounded border-orange-300 text-orange-500 focus:ring-orange-400 disabled:opacity-40 cursor-pointer"
              title={item.enabled ? 'Exclude from exports' : 'Include in exports'}
            />
            <span className="shrink-0 font-mono text-[10px] font-bold text-orange-700 bg-orange-100 border border-orange-200 rounded px-1 py-px">
              {item.external_id}
            </span>
            <div className="flex-1 min-w-0 flex items-center gap-1.5">
              <p className="text-[11px] font-medium text-slate-700 truncate leading-tight">
                {item.name}
              </p>
              {item.item_category && (
                <span className="shrink-0 text-[9px] text-slate-400 uppercase tracking-wider">
                  {item.item_category}
                </span>
              )}
              {item.quantity_edited && (
                <span
                  className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-100 rounded px-1 py-px"
                  title="Manually edited — no longer follows the calculated quantity"
                >
                  Edited
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Qty</span>
              <NumericInput
                value={item.quantity}
                onChange={(qty) => handleQuantityChange(item.id, qty)}
                min={1} max={999}
                disabled={!isEnabled || !item.enabled}
                className="w-11 rounded border border-orange-200 bg-white px-1.5 py-0.5 text-[10px] text-center font-semibold text-slate-700 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none disabled:bg-slate-100 disabled:text-slate-400"
              />
            </div>
          </div>
        ))}

        {missingExternalIds.map(externalId => (
          <div
            key={externalId}
            className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50/40 px-2.5 py-1.5"
          >
            <span className="shrink-0 font-mono text-[10px] font-bold text-red-700 bg-red-100 border border-red-200 rounded px-1 py-px">
              {externalId}
            </span>
            <p className="text-[11px] text-red-600">
              Not found in the client database — import it into the NetSuite items or add it manually
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
